import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { palette } from '../../utils/palette';
import Button from '../common/Button';
import type { HarmonyMode } from '../../hooks/usePaletteGeneration';

const HARMONY_MODES: HarmonyMode[] = [
  'random',
  'complementary',
  'triadic',
  'analogous',
];

interface CountStepperProps {
  label: string;
  value: string;
  onDecrement: () => void;
  onIncrement: () => void;
  decrementDisabled: boolean;
  incrementDisabled: boolean;
  valueSuffix: string;
  themeColors: {
    text: string;
    primary: string;
    secondary: string;
    border: string;
  };
}

function CountStepper({
  label,
  value,
  onDecrement,
  onIncrement,
  decrementDisabled,
  incrementDisabled,
  valueSuffix,
  themeColors,
}: CountStepperProps) {
  return (
    <View style={styles.countInputContainer}>
      <Text
        style={[styles.countLabel, { color: themeColors.text }]}
        numberOfLines={1}
      >
        {label}
      </Text>
      <View
        style={[
          styles.combinationSelector,
          { backgroundColor: themeColors.border + '80' },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.selectorButton,
            {
              backgroundColor: themeColors.border,
              opacity: decrementDisabled ? 0.5 : 1,
            },
          ]}
          onPress={onDecrement}
          disabled={decrementDisabled}
        >
          <Ionicons
            name="remove"
            size={14}
            color={decrementDisabled ? themeColors.secondary : themeColors.text}
          />
        </TouchableOpacity>
        <Text style={[styles.selectorValue, { color: themeColors.text }]}>
          {value}
        </Text>
        <TouchableOpacity
          style={[
            styles.selectorButton,
            {
              backgroundColor: themeColors.border,
              opacity: incrementDisabled ? 0.5 : 1,
            },
          ]}
          onPress={onIncrement}
          disabled={incrementDisabled}
        >
          <Ionicons
            name="add"
            size={14}
            color={incrementDisabled ? themeColors.secondary : themeColors.text}
          />
        </TouchableOpacity>
      </View>
      <Text
        style={[styles.countLabel, { color: themeColors.text }]}
        numberOfLines={1}
      >
        {valueSuffix}
      </Text>
    </View>
  );
}

export interface CreatePalettesCardProps {
  isPublicPreview?: boolean;
  canUseHarmony?: boolean;
  hideUseMyPalette?: boolean;
  recommendedColorsCount: number;
  selectedColorsLength: number;
  maxSelection: number;
  generationCount: string;
  setGenerationCount: (v: string) => void;
  combinationCount: string;
  setCombinationCount: (v: string) => void;
  generationCountMax: number;
  combinationCountMax: number;
  harmonyMode: HarmonyMode;
  setHarmonyMode: (m: HarmonyMode) => void;
  onUseMyPalette: () => void;
  onRandomize: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
  isMobile: boolean;
  isTablet: boolean;
}

const CreatePalettesCard: React.FC<CreatePalettesCardProps> = ({
  isPublicPreview = false,
  canUseHarmony = false,
  hideUseMyPalette = false,
  recommendedColorsCount,
  selectedColorsLength,
  maxSelection: _maxSelection,
  generationCount,
  setGenerationCount,
  combinationCount,
  setCombinationCount,
  generationCountMax,
  combinationCountMax,
  harmonyMode,
  setHarmonyMode,
  onUseMyPalette,
  onRandomize,
  onGenerate,
  isGenerating,
  isMobile,
  isTablet,
}) => {
  const { colors } = useTheme();
  const genNum = parseInt(generationCount, 10);
  const comboNum = parseInt(combinationCount, 10);
  const maxGenForSelection = Math.min(
    generationCountMax,
    selectedColorsLength || generationCountMax
  );

  return (
    <View
      style={[
        styles.generationCard,
        isMobile && styles.generationCardMobile,
        {
          backgroundColor: colors.border + '30',
          borderColor: colors.border,
        },
      ]}
      accessibilityLabel="Create palettes"
    >
      <Text style={[styles.cardHeader, { color: colors.text }]}>
        Create palettes
      </Text>
      <Text style={[styles.randomizeHint, { color: colors.secondary }]}>
        {isPublicPreview
          ? 'Tap Randomize for a curated palette'
          : 'Pick colours above or use Randomize.'}
      </Text>

      {canUseHarmony && !isPublicPreview && (
        <View style={styles.harmonyRow}>
          <Text style={[styles.harmonyLabel, { color: colors.secondary }]}>
            Harmony
          </Text>
          <View
            style={[
              styles.harmonySegments,
              { backgroundColor: colors.border + '50' },
            ]}
          >
            {HARMONY_MODES.map((mode) => (
              <TouchableOpacity
                key={mode}
                style={[
                  styles.harmonySegment,
                  harmonyMode === mode && {
                    backgroundColor: colors.primary + '22',
                  },
                ]}
                onPress={() => setHarmonyMode(mode)}
                activeOpacity={0.8}
              >
                <Text
                  style={[
                    styles.harmonySegmentText,
                    {
                      color:
                        harmonyMode === mode ? colors.primary : colors.text,
                    },
                  ]}
                  numberOfLines={1}
                >
                  {mode === 'random'
                    ? 'Random'
                    : mode.charAt(0).toUpperCase() + mode.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      <View style={styles.useMyPaletteRandomizeRow}>
        {!hideUseMyPalette && !isPublicPreview && (
          <Button
            title={`Use My Palette${recommendedColorsCount > 0 ? ` (${recommendedColorsCount})` : ''}`}
            variant="outline-purple"
            icon={
              <Ionicons name="heart" size={12} color={palette.outlinePurple} />
            }
            iconPosition="left"
            allowTextWrap={false}
            adjustsFontSizeToFit
            textStyle={styles.cardButtonText}
            onPress={onUseMyPalette}
            style={[
              styles.useMyPaletteButton,
              styles.useMyPaletteButtonSideBySide,
              isMobile ? styles.useMyPaletteButtonMobile : undefined,
            ]}
            trackClick
            trackingLocation="color-generator-screen"
            trackingData={{
              button_name: 'Use My Palette',
              button_type: 'use_my_palette',
              screen: 'color_generator',
            }}
          />
        )}
        <Button
          title="Randomize"
          variant="secondary"
          onPress={onRandomize}
          icon={<Ionicons name="shuffle" size={14} color={colors.text} />}
          iconPosition="left"
          textStyle={styles.cardButtonText}
          trackClick
          trackingLocation="color-generator-screen"
          trackingData={{
            button_name: 'Randomize',
            button_type: 'randomize',
            screen: 'color_generator',
          }}
          style={[
            styles.generateButton,
            styles.randomizeButtonSideBySide,
            isMobile ? styles.generateButtonMobile : undefined,
            isTablet ? styles.generateButtonTablet : undefined,
          ]}
        />
      </View>

      {!isPublicPreview && (
        <View style={styles.generationSection}>
          <View style={styles.generationHeader}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              From your colours
            </Text>
            <Text style={[styles.generationInfo, { color: colors.secondary }]}>
              {selectedColorsLength > 0
                ? `${generationCount} color${genNum !== 1 ? 's' : ''} per palette · ${combinationCount} palette${comboNum !== 1 ? 's' : ''}`
                : 'Select colors above to generate'}
            </Text>
          </View>
          <View
            style={[
              styles.generationControls,
              isMobile && styles.generationControlsMobile,
              isTablet && styles.generationControlsTablet,
            ]}
          >
            {isMobile ? (
              <>
                <CountStepper
                  label="Generate"
                  value={generationCount}
                  onDecrement={() =>
                    setGenerationCount(String(Math.max(1, genNum - 1)))
                  }
                  onIncrement={() =>
                    setGenerationCount(
                      String(Math.min(maxGenForSelection, genNum + 1))
                    )
                  }
                  decrementDisabled={genNum <= 1}
                  incrementDisabled={genNum >= maxGenForSelection}
                  valueSuffix={`color${genNum !== 1 ? 's' : ''}`}
                  themeColors={colors}
                />
                <CountStepper
                  label="Try"
                  value={combinationCount}
                  onDecrement={() =>
                    setCombinationCount(String(Math.max(1, comboNum - 1)))
                  }
                  onIncrement={() =>
                    setCombinationCount(
                      String(Math.min(combinationCountMax, comboNum + 1))
                    )
                  }
                  decrementDisabled={comboNum <= 1}
                  incrementDisabled={comboNum >= combinationCountMax}
                  valueSuffix={`combo${comboNum !== 1 ? 's' : ''}`}
                  themeColors={colors}
                />
              </>
            ) : (
              <View style={styles.controlsRow}>
                <CountStepper
                  label="Generate"
                  value={generationCount}
                  onDecrement={() =>
                    setGenerationCount(String(Math.max(1, genNum - 1)))
                  }
                  onIncrement={() =>
                    setGenerationCount(
                      String(Math.min(maxGenForSelection, genNum + 1))
                    )
                  }
                  decrementDisabled={genNum <= 1}
                  incrementDisabled={genNum >= maxGenForSelection}
                  valueSuffix={`color${genNum !== 1 ? 's' : ''}`}
                  themeColors={colors}
                />
                <CountStepper
                  label="Try"
                  value={combinationCount}
                  onDecrement={() =>
                    setCombinationCount(String(Math.max(1, comboNum - 1)))
                  }
                  onIncrement={() =>
                    setCombinationCount(
                      String(Math.min(combinationCountMax, comboNum + 1))
                    )
                  }
                  decrementDisabled={comboNum <= 1}
                  incrementDisabled={comboNum >= combinationCountMax}
                  valueSuffix={`combo${comboNum !== 1 ? 's' : ''}`}
                  themeColors={colors}
                />
              </View>
            )}
            <Button
              title="Generate"
              variant="primary"
              disabled={selectedColorsLength === 0}
              loading={isGenerating}
              onPress={onGenerate}
              textStyle={styles.cardButtonText}
              icon={
                <Ionicons
                  name="sparkles"
                  size={16}
                  color={
                    selectedColorsLength > 0
                      ? '#FFFFFF'
                      : palette.verySoftViolet
                  }
                />
              }
              iconPosition="left"
              trackClick={true}
              trackingLocation="color-generator-screen"
              trackingData={{
                button_name: 'Generate',
                button_type: 'generate',
                screen: 'color_generator',
              }}
              style={[
                styles.generateButton,
                isMobile ? styles.generateButtonMobile : undefined,
                isTablet ? styles.generateButtonTablet : undefined,
              ]}
              accessibilityLabel="Generate palette from your colours"
            />
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  generationCard: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    marginTop: 24,
    marginBottom: 24,
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
  },
  generationCardMobile: {
    borderWidth: 0,
    borderColor: 'transparent',
    paddingLeft: 0,
    paddingRight: 0,
    paddingHorizontal: 0,
    marginBottom: 16,
  },
  cardHeader: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 2,
    letterSpacing: -0.2,
  },
  randomizeHint: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 10,
    opacity: 0.9,
  },
  harmonyRow: {
    width: '100%',
    marginBottom: 8,
    gap: 6,
  },
  harmonyLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  harmonySegments: {
    flexDirection: 'row',
    borderRadius: 8,
    padding: 3,
    flexWrap: 'wrap',
    gap: 4,
  },
  harmonySegment: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  harmonySegmentText: {
    fontSize: 13,
    fontWeight: '600',
  },
  useMyPaletteRandomizeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 8,
    width: '100%',
  },
  useMyPaletteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 44,
    borderRadius: 10,
    gap: 5,
  },
  useMyPaletteButtonSideBySide: {
    flex: 1,
    minWidth: 120,
  },
  randomizeButtonSideBySide: {
    flex: 1,
    minWidth: 120,
  },
  useMyPaletteButtonMobile: {
    justifyContent: 'center',
  },
  cardButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  generationSection: {
    gap: 6,
    width: '100%',
    alignItems: 'flex-start',
  },
  generationHeader: {
    gap: 2,
    alignItems: 'flex-start',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  generationInfo: {
    fontSize: 12,
  },
  generationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  generationControlsMobile: {
    flexDirection: 'column',
    gap: 12,
    alignItems: 'flex-start',
    width: '100%',
  },
  generationControlsTablet: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 44,
    borderRadius: 10,
    gap: 5,
    ...(Platform.OS === 'web' && { cursor: 'pointer' as const }),
  },
  generateButtonMobile: {
    justifyContent: 'center',
  },
  generateButtonTablet: {
    flexShrink: 0,
    width: undefined,
  },
  controlsRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  countInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  countLabel: {
    fontSize: 12,
    flexShrink: 0,
    fontWeight: '600',
  },
  combinationSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 6,
    padding: 2,
    gap: 2,
    flexShrink: 0,
  },
  selectorButton: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectorValue: {
    fontSize: 13,
    fontWeight: '600',
    minWidth: 18,
    textAlign: 'center',
  },
});

export default CreatePalettesCard;
